import Enumerable from "linq";
import { AsyncUtility } from "./AsyncUtility";

export interface IAsyncRequest<TResult, TSource> {
  id: number;
  requester: any;
  request: (source: TSource) => Promise<TResult>;
  source: TSource;
}

export class AsyncRequestPool<TResult, TSource> {
  constructor(private readonly _processed: (source: IAsyncRequest<TResult, TSource>) => void = null) {
    this._requests = [];
    this._lastPublishedId = 0;
    this._processingRequest = null;
  }
  private _requests: IAsyncRequest<TResult, TSource>[];
  private _lastPublishedId: number; // numberで表現しきれないほどのrequestを処理する事はない想定.(そうでない場合は利用しない事.)
  private _processingRequest: IAsyncRequest<TResult, TSource>;

  public get isProcessing(): boolean {
    return !!this.processingRequest;
  }

  public get processingRequest(): IAsyncRequest<TResult, TSource> {
    return this._processingRequest;
  }

  public async request(source: TSource, requester: any, request: (source: TSource) => Promise<TResult>): Promise<TResult> {
    return this.process(
      await this.waitProcessible(
        this.push(
          this.create(source, requester, request))));
  }

  private async process(request: IAsyncRequest<TResult, TSource>): Promise<TResult> {
    if (!request) {
      return null;
    }
    this._processingRequest = request;
    const result = await request.request(request.source);
    this.notifyProcessed(request);
    if (this.areRequestsEqual(this._processingRequest, request)) {
      this._processingRequest = null;
    }
    return result;      
  }

  private notifyProcessed(request: IAsyncRequest<TResult, TSource>): void {
    if (this._processed) {
      this._processed(request);
    }
  }

  private async waitProcessible(request: IAsyncRequest<TResult, TSource>): Promise<IAsyncRequest<TResult, TSource>> {
    await AsyncUtility.wait(() => !this._processingRequest || !this.isRequestWaiting(request));
    return this.pop(request);
  }

  private create(source: TSource, requester: any, request: (source: TSource) => Promise<TResult>): IAsyncRequest<TResult, TSource> {
    return {
      id: this.publishId(),
      requester,
      request,
      source,
    };
  }

  private publishId(): number {
    return ++this._lastPublishedId;
  }

  private push(request: IAsyncRequest<TResult, TSource>): IAsyncRequest<TResult, TSource> {
    this._requests.push(request);
    return request;
  }

  private pop(request: IAsyncRequest<TResult, TSource>): IAsyncRequest<TResult, TSource> {
    const popped = Enumerable.from(this._requests)
      .firstOrDefault(r => this.areRequestsEqual(r, request));
    this._requests = Enumerable.from(this._requests)
      .where(r => !this.areRequestsEqual(r, popped))
      .toArray();
    return popped;
  }

  private isRequestWaiting(request: IAsyncRequest<TResult, TSource>): boolean {
    return Enumerable.from(this._requests)
      .any(r => this.areRequestsEqual(r, request));
  }

  private areRequestsEqual(source: IAsyncRequest<TResult, TSource>, destination: IAsyncRequest<TResult, TSource>) {
    return (source === destination)
      || (source && destination && (source.id === destination.id));
  }

  public cancel(requester: any = null): boolean {
    // 実行待ちのリクエストは全て破棄.
    this._requests = Enumerable.from(this._requests)
      .where(r => requester && (r.requester !== requester))
      .toArray();
    // 実行中のリクエストはキャンセル.
    if (this._processingRequest && (!requester || (this._processingRequest.requester === requester))) {
      this._processingRequest = null;
      return true;  // 実行中リクエストキャンセルした場合はtrueを返す.
    }
    return false;
  }
}
